import { useSuspenseQuery } from "@tanstack/react-query";
import { Suspense } from "react";
import { useParams } from "react-router-dom";

import { UserIdentitiesResponse } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import { Authorizations } from "~/api/types";
import { fetchSiteIntegrationsWithAuth } from "~/hooks/useGoogleDashboard";
import { QUERY_KEYS } from "~/pages/GoogleDashboardPage/constants";

import { ConnectedAccounts } from "./ConnectedAccounts/ConnectedAccounts";
import { ConnectedAccountsSkeleton } from "./ConnectedAccounts/ConnectedAccountsSkeleton";
import styles from "./GoogleIntegration.module.scss";
import { SiteIntegrations } from "./SiteIntegrations/SiteIntegrations";
import { SiteIntegrationsSkeleton } from "./SiteIntegrations/SiteIntegrationsSkeleton";
import { WITH_AUTHORIZATIONS } from "./withAuthorizations";

type UserIdentitiesResponseWithAuth = UserIdentitiesResponse & {
  authorizations?: Authorizations;
};

const GoogleIntegrationInternal = () => {
  const { id } = useParams();
  const siteId = Number(id);

  const { data: identities } = useSuspenseQuery({
    queryKey: QUERY_KEYS.USER_IDENTITIES,
    queryFn: async () => {
      const response = await apiClient.userIdentities.userIdentitiesList(
        {
          "page[number]": 1,
          "page[size]": 100,
        },
        {
          headers: {
            ...WITH_AUTHORIZATIONS.USER_IDENTITIES.getRequestHeaders(),
          },
        },
      );

      const data = response.data as UserIdentitiesResponseWithAuth;

      return {
        user_identities: data.user_identities,
        authorizations: data.authorizations,
      };
    },
    select: (data) => {
      return WITH_AUTHORIZATIONS.USER_IDENTITIES.resolveAuthorizations(
        data.user_identities,
        data.authorizations,
      ).filter((item) => item.record.kind === "analytics");
    },
  });

  const { data: integrations } = useSuspenseQuery({
    queryKey: QUERY_KEYS.SITE_INTEGRATIONS(siteId),
    queryFn: () => fetchSiteIntegrationsWithAuth(siteId),
    select: (data) => {
      return WITH_AUTHORIZATIONS.SITE_INTEGRATIONS.resolveAuthorizations(
        data.integrations,
        data.authorizations,
      );
    },
  });

  return (
    <div className={styles.root}>
      <SiteIntegrations
        siteId={siteId}
        integrations={integrations}
        identities={identities.map((item) => item.record)}
      />
      <ConnectedAccounts identities={identities} siteId={siteId} />
    </div>
  );
};

export const GoogleIntegration = () => {
  return (
    <Suspense
      fallback={
        <div className={styles.root}>
          <SiteIntegrationsSkeleton />
          <ConnectedAccountsSkeleton />
        </div>
      }
    >
      <GoogleIntegrationInternal />
    </Suspense>
  );
};
